/**
 * Page-mode slot rendered by `<CallPage>` in place of the join prompt when a
 * visit left an artifact behind but never produced a note.
 *
 * Three shapes of the same recovery: the AI draft reached storage but the client
 * went away before opening it (`draft-ready`), generation failed outright and
 * only the transcript survived (`transcript-only`), or a generation is running
 * right now (`generating` — PD-1378 — a status, not an action). All beat the
 * alternative: a bare "Join call" prompt that hides that any work happened.
 *
 * Same fixed footprint as `<ZoomPanelPreJoin>` / `<ExistingNoteNotice>`.
 */
import {
  IconAlertTriangle,
  IconExternalLink,
  IconFileText,
  IconRefresh,
  IconSparkles,
  IconVideoPlus,
} from '@tabler/icons-react'
import { CHART_CONTENT_HEIGHT_VAR, CHART_CONTENT_TOP_VAR } from '@/pages/Patient/chartTopOffset'
import { SCRIBE_ACCENT } from './scribeText'

type DraftRecoveryMode = 'draft-ready' | 'transcript-only' | 'generating'

export type DraftRecoveryNoticeProps = {
  mode: DraftRecoveryMode
  /** Panel width (px) — matches the live rail so the slot lines up. */
  width: number
  /** Patient display name. Falls back to "this visit". */
  patientName?: string
  /** Pre-signed transcript URL; the link is hidden when absent. */
  transcriptUrl?: string | null
  /** Disables the primary action while a generation is in flight. */
  busy?: boolean
  onPrimary?: () => void
  /** Replaces the primary action for viewers who can never open the note
      (managers — the Progress Note form never mounts for them). */
  primaryUnavailableNotice?: string
  /**
   * Escape back into the call. This slot replaces the join prompt, so without it
   * a doctor whose note failed could not get back to a patient who is still
   * waiting — the visit isn't necessarily over just because generation broke.
   */
  onRejoin?: () => void
}

const COPY: Record<
  DraftRecoveryMode,
  {
    icon: typeof IconSparkles
    tint: string
    title: string
    body: string
    /** Both omitted ⇒ informational card: no primary action is rendered. */
    actionIcon?: typeof IconSparkles
    action?: string
  }
> = {
  'draft-ready': {
    icon: IconSparkles,
    actionIcon: IconFileText,
    tint: 'border-amber-400/30 text-amber-300',
    title: 'Progress Note ready',
    body: 'The AI note for {patient} was generated but never opened. Open it to review and sign.',
    action: 'Open the note',
  },
  'transcript-only': {
    icon: IconAlertTriangle,
    actionIcon: IconRefresh,
    tint: 'border-[#e8624a]/40 text-[#e8624a]',
    title: "Note wasn't generated",
    body: 'The conversation with {patient} was saved. Generate the note from it — nothing was lost.',
    action: 'Retry',
  },
  generating: {
    icon: IconSparkles,
    tint: 'border-sky-400/30 text-sky-300',
    title: 'Progress note being generated',
    body: "A draft is already being generated for this visit. It will appear here when it's ready.",
  },
}

/** What the rail receives; width + patient are supplied by the panel itself. */
export type DraftRecoveryProps = Omit<DraftRecoveryNoticeProps, 'width' | 'patientName'>

/**
 * What CallPage decides: which artifact exists and how to get back into the
 * call. It stops there because running the recovery needs the scribe form
 * bridge, which `<EncounterWorkspace>` provides one level below — so the
 * workspace turns this into {@link DraftRecoveryProps} by adding the action.
 */
interface DraftRecoveryBase {
  transcriptUrl: string | null
  onRejoin?: () => void
}

export type DraftRecoveryRequest =
  | (DraftRecoveryBase & { mode: 'draft-ready'; draftDocRefId: string })
  | (DraftRecoveryBase & { mode: 'transcript-only'; transcriptDocRefId: string })
  // No artifact id: nothing to act on until the running generation lands.
  | (DraftRecoveryBase & { mode: 'generating' })

export function DraftRecoveryNotice({
  mode,
  width,
  patientName,
  transcriptUrl,
  busy,
  onPrimary,
  primaryUnavailableNotice,
  onRejoin,
}: DraftRecoveryNoticeProps) {
  const copy = COPY[mode]
  const Icon = copy.icon
  // Spinner while working, otherwise the action's own icon — a refresh arrow on
  // "Open the note" would read as a retry.
  const ActionIcon = busy ? IconRefresh : copy.actionIcon

  return (
    <aside
      data-testid="zoom-panel-draft-recovery"
      data-mode={mode}
      className="fixed flex flex-col items-center justify-center gap-5 overflow-hidden bg-[#0f172a] px-6 text-center text-slate-200"
      style={{
        top: CHART_CONTENT_TOP_VAR,
        right: 0,
        width: `${width}px`,
        height: CHART_CONTENT_HEIGHT_VAR,
        zIndex: 'var(--z-scribe)' as unknown as number,
        borderLeft: '1px solid #1e293b',
      }}
    >
      <div
        className={`flex h-20 w-20 items-center justify-center rounded-full border-2 ${copy.tint}`}
        style={{ background: 'linear-gradient(135deg, #475569, #334155)' }}
      >
        <Icon size={34} />
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-base font-semibold text-slate-100">{copy.title}</span>
        <span className="max-w-[240px] text-[13px] leading-snug text-slate-400">
          {copy.body.replace('{patient}', patientName ?? 'this visit')}
        </span>
      </div>

      <div className="flex w-full max-w-[240px] flex-col items-center gap-3">
        {/* An action-less mode ('generating') has nothing to offer — not even the
            manager notice, which explains a button that isn't there. */}
        {copy.action && ActionIcon ? (
          primaryUnavailableNotice ? (
            <div
              data-testid="zoom-panel-draft-recovery-unavailable"
              className="w-full rounded-lg border border-white/10 px-4 py-2.5 text-[13px] leading-snug text-slate-400"
            >
              {primaryUnavailableNotice}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => onPrimary?.()}
              disabled={busy}
              data-testid="zoom-panel-draft-recovery-primary"
              className="flex w-full items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
              style={{ background: SCRIBE_ACCENT }}
            >
              <ActionIcon size={18} className={busy ? 'animate-spin' : undefined} />
              {busy ? 'Generating…' : copy.action}
            </button>
          )
        ) : null}

        {onRejoin ? (
          <button
            type="button"
            onClick={onRejoin}
            data-testid="zoom-panel-draft-recovery-rejoin"
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-white/15 px-5 py-2.5 text-[13px] font-semibold text-slate-200 hover:bg-white/5"
          >
            <IconVideoPlus size={17} />
            Rejoin call
          </button>
        ) : null}

        {/* Last: the two buttons are what to DO, this is a read-only aside.
            A plain anchor, not a click handler — opening after an await would
            lose the user gesture and trip the popup blocker. */}
        {transcriptUrl ? (
          <a
            href={transcriptUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="zoom-panel-draft-recovery-transcript"
            className="mt-1 flex items-center gap-1.5 text-[13px] font-semibold text-sky-400 hover:text-sky-300"
          >
            View transcript
            <IconExternalLink size={15} />
          </a>
        ) : null}
      </div>
    </aside>
  )
}
